import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SetupScreen } from "@/components/SetupScreen";
import { LockScreen } from "@/components/LockScreen";
import { AppShell } from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import AddTransaction from "@/pages/AddTransaction";
import TransactionDetail from "@/pages/TransactionDetail";
import BudgetPage from "@/pages/BudgetPage";
import BillsPage from "@/pages/BillsPage";
import MorePage from "@/pages/MorePage";
import SettingsPage from "@/pages/SettingsPage";
import ReportsPage from "@/pages/ReportsPage";
import AssetsPage from "@/pages/AssetsPage";
import AccountsPage from "@/pages/AccountsPage";
import SavingsGoalsPage from "@/pages/SavingsGoalsPage";
import TaxWizardPage from "@/pages/TaxWizardPage";
import MedicareHealthPage from "@/pages/MedicareHealthPage";
import VehiclesPage from "@/pages/VehiclesPage";
import SavingsChallengesPage from "@/pages/SavingsChallengesPage";
import ReferralPage from "@/pages/ReferralPage";
import BankSyncPage from "@/pages/BankSyncPage";
import InceptionGlenPage from "@/pages/InceptionGlenPage";
import CashFlowPage from "@/pages/CashFlowPage";
import YearInReviewPage from "@/pages/YearInReviewPage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";
import SupportPage from "@/pages/SupportPage";
import { InstallBanner } from "@/components/InstallBanner";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminPage from "@/pages/AdminPage";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

function AuthGate() {
  const { isSetup, isUnlocked } = useAuth();

  if (!isSetup) return <SetupScreen />;
  if (!isUnlocked) return <LockScreen />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/transactions/add" element={<AddTransaction />} />
        <Route path="/transactions/:id" element={<TransactionDetail />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/bills" element={<BillsPage />} />
        <Route path="/more" element={<MorePage />} />
        <Route path="/more/settings" element={<SettingsPage />} />
        <Route path="/more/reports" element={<ReportsPage />} />
        <Route path="/more/assets" element={<AssetsPage />} />
        <Route path="/more/savings" element={<SavingsGoalsPage />} />
        <Route path="/more/tax-wizard" element={<TaxWizardPage />} />
        <Route path="/more/medicare" element={<MedicareHealthPage />} />
        <Route path="/more/vehicles" element={<VehiclesPage />} />
        <Route path="/more/challenges" element={<SavingsChallengesPage />} />
        <Route path="/more/referral" element={<ReferralPage />} />
        <Route path="/more/bank-sync" element={<BankSyncPage />} />
        <Route path="/more/inception-glen" element={<InceptionGlenPage />} />
        <Route path="/more/cash-flow" element={<CashFlowPage />} />
        <Route path="/more/year-in-review" element={<YearInReviewPage />} />
        <Route path="/more/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/more/support" element={<SupportPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/more/*" element={<MorePage />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <InstallBanner />
      <BrowserRouter>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<AuthProvider><AuthGate /></AuthProvider>} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
